import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../styles/colors";

const Nav = styled(motion.nav)`

  position: fixed;
  font-family: "Montserrat";
  top: 0;
  left: 0;
  right: 0;
  height: 130px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
  z-index: 1000;
  font-size: 18px;
  background-image: url("/images/menu.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
`;

const NavContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ee821a;
  padding: 20px;
  border-radius: 40px;

`;

const Logo = styled.a`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${(props) =>
        props.scrolled ? colors.text.primary : colors.text.light};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 40px;

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }

  span {
    color: ${(props) => (props.scrolled ? "#4396CC" : "#FC860A")};
    transition: color 0.3s ease;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  transition: color 0.3s ease;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${(props) => (props.active ? "100%" : "0")};
    height: 2px;
    background: ${colors.gradients.primary};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.primary.main};
    &:after {
      width: 100%;
    }
  }

  ${(props) =>
        props.active &&
        `
    color: ${colors.primary.main};
    font-weight: 600;
  `}
`;

const ExternalLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${colors.gradients.primary};
  border-radius: 20px;
  color: ${colors.text.light} !important;
  font-weight: 600;

  &:after {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px ${colors.shadow.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${(props) =>
        props.scrolled ? colors.text.primary : colors.text.light};
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: ${colors.background.primary};
  padding: 1rem;
  box-shadow: 0 2px 20px ${colors.shadow.primary};
`;

const MobileNavLink = styled.a`
  display: block;
  text-decoration: none;
  padding: 1rem;
  text-align: center;
  font-weight: 500;
  border-bottom: 1px solid #eee;
  background: ${(props) =>
        props.active ? colors.background.secondary : "transparent"};
  color: ${(props) =>
        props.active ? colors.primary.main : colors.text.primary};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.background.secondary};
    color: ${colors.primary.main};
  }
`;

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState("hero");

    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            setScrolled(offset > 50);

            // Detectar sección activa
            const sections = [
                "hero",
                "about",
                "features",
                "gallery",
                "testimonials",
                "contact",
                "manage-content",
                "download",
            ];
            let currentSection = sections[0];

            sections.forEach((section) => {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom >= 100) {
                        currentSection = section;
                    }
                }
            });

            setActiveSection(currentSection);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 130; // altura del navbar
            const elementPosition = element.offsetTop;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: "smooth",
            });
        }
        setMobileMenuOpen(false);
        setActiveSection(sectionId);
    };

    return (
        <>
            <Nav
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5 }}
                scrolled={scrolled}
            >
                <Logo
                    scrolled={scrolled}
                    href="#"
                    onClick={() => scrollToSection("hero")}
                >
                    <img src="/images/ped.png" alt="PedDigital Logo" />
                </Logo>
                <NavContainer>
                    <NavLinks>
                        <NavLink
                            scrolled={scrolled}
                            href="#about"
                            active={activeSection === "about"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("about");
                            }}
                        >
                            Sobre Nosotros
                        </NavLink>
                        <NavLink
                            scrolled={scrolled}
                            href="#features"
                            active={activeSection === "features"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("features");
                            }}
                        >
                            Características
                        </NavLink>
                        <NavLink
                            scrolled={scrolled}
                            href="#gallery"
                            active={activeSection === "gallery"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("gallery");
                            }}
                        >
                            Galería
                        </NavLink>
                        <NavLink
                            scrolled={scrolled}
                            href="#testimonials"
                            active={activeSection === "testimonials"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("testimonials");
                            }}
                        >
                            Testimonios
                        </NavLink>

                        <NavLink
                            scrolled={scrolled}
                            href="#manage-content"
                            active={activeSection === "manage-content"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("manage-content");
                            }}
                        >
                            Gestionar Contenido
                        </NavLink>
                        <NavLink
                            scrolled={scrolled}
                            href="#download"
                            active={activeSection === "download"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("download");
                            }}
                        >
                            Descargar
                        </NavLink>
                        <NavLink
                            scrolled={scrolled}
                            href="#contact"
                            active={activeSection === "contact"}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("contact");
                            }}
                        >
                            Contáctanos
                        </NavLink>
                    </NavLinks>

                    <MobileMenuButton
                        scrolled={scrolled}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? "✕" : "☰"}
                    </MobileMenuButton>
                </NavContainer>
            </Nav>

            <AnimatePresence>
                {mobileMenuOpen && (
                    <MobileMenu
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <MobileNavLink
                            href="#about"
                            active={activeSection === "about"}
                            onClick={() => scrollToSection("about")}
                        >
                            Sobre Nosotros
                        </MobileNavLink>
                        <MobileNavLink
                            href="#features"
                            active={activeSection === "features"}
                            onClick={() => scrollToSection("features")}
                        >
                            Características
                        </MobileNavLink>
                        <MobileNavLink
                            href="#gallery"
                            active={activeSection === "gallery"}
                            onClick={() => scrollToSection("gallery")}
                        >
                            Galería
                        </MobileNavLink>
                        <MobileNavLink
                            href="#testimonials"
                            active={activeSection === "testimonials"}
                            onClick={() => scrollToSection("testimonials")}
                        >
                            Testimonios
                        </MobileNavLink>

                        <MobileNavLink
                            href="#manage-content"
                            active={activeSection === "manage-content"}
                            onClick={() => scrollToSection("manage-content")}
                        >
                            Gestionar Contenido
                        </MobileNavLink>
                        <MobileNavLink
                            href="#download"
                            active={activeSection === "download"}
                            onClick={() => scrollToSection("download")}
                        >
                            Descargar
                        </MobileNavLink>
                        <MobileNavLink
                            href="#contact"
                            active={activeSection === "contact"}
                            onClick={() => scrollToSection("contact")}
                        >
                            Contáctanos
                        </MobileNavLink>
                    </MobileMenu>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
